import Segmented from "@/components/Segmented";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { http } from "@/lib/api";
import { useMensagens } from "@/context/MensagensContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { MessageSquare, Plus, Send, Paperclip, ArrowLeft, Users, UserPlus, Check, X, CalendarClock, FolderKanban, LogOut, Inbox } from "lucide-react";

import { Dica } from "@/components/Dica";
const MAX = 2000;

const fmtHora = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const hoje = new Date();
    return d.toDateString() === hoje.toDateString()
      ? d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })
      : d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
  } catch { return ""; }
};
const fmtData = (iso) => { try { return new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString("pt-BR"); } catch { return iso || ""; } };

// Lote 90 — Mensagens: conversas com a Equipe (cadastrada na aba Equipe) e com "Terceiros" (colegas com
// conta no IApauta, vinculados com aceite dos dois lados). 1 a 1 e grupos; anexo = resumo sem nomes de clientes.
export default function Mensagens() {
  const [params, setParams] = useSearchParams();
  const { refresh } = useMensagens();
  const [aba, setAba] = useState(params.get("aba") === "vinculos" ? "vinculos" : "conversas");
  const [threads, setThreads] = useState(null);
  const [resumo, setResumo] = useState({ equipe: [], terceiros: [], recebidos: [], enviados: [] });
  const [aberta, setAberta] = useState(null); // { thread, messages }
  const [texto, setTexto] = useState("");
  const [anexo, setAnexo] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [novaOpen, setNovaOpen] = useState(false);
  const [anexoOpen, setAnexoOpen] = useState(false);
  const fimRef = useRef(null);
  const threadId = params.get("t");

  const loadThreads = useCallback(() => http.get("/msg/threads").then((r) => setThreads(r.data)).catch(() => setThreads((t) => t || [])), []);
  const loadResumo = useCallback(() => http.get("/msg/resumo").then((r) => setResumo(r.data)).catch(() => {}), []);
  const loadAberta = useCallback(async () => {
    if (!threadId) { setAberta(null); return; }
    try {
      const r = await http.get(`/msg/threads/${threadId}`);
      setAberta(r.data);
      refresh();
    } catch { setAberta(null); }
  }, [threadId, refresh]);

  useEffect(() => { loadThreads(); loadResumo(); }, [loadThreads, loadResumo]);
  useEffect(() => { loadAberta(); }, [loadAberta]);
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === "hidden") return;
      loadThreads();
      if (threadId) loadAberta();
    }, 8000);
    return () => clearInterval(id);
  }, [loadThreads, loadAberta, threadId]);
  useEffect(() => { fimRef.current?.scrollIntoView({ block: "end" }); }, [aberta?.messages?.length]);

  const abrir = (id) => { setParams(id ? { t: id } : {}, { replace: false }); setTexto(""); setAnexo(null); };

  const enviar = async () => {
    if (!aberta || enviando || (!texto.trim() && !anexo)) return;
    setEnviando(true);
    try {
      const r = await http.post(`/msg/threads/${aberta.thread.thread_id}/mensagens`, {
        text: texto,
        anexo: anexo ? { tipo: anexo.tipo, id: anexo.id } : null,
      });
      setTexto(""); setAnexo(null);
      if (aberta.thread.externos?.length) {
        toast.success(r.data.externos_email === aberta.thread.externos.length ? "Enviada (e-mail para quem não tem conta)" : "Enviada — e-mail pode não ter chegado a alguém da Equipe");
      }
      await loadAberta(); loadThreads();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível enviar");
    } finally { setEnviando(false); }
  };

  const sair = async () => {
    if (!aberta || !window.confirm("Sair desta conversa? Você deixa de receber as mensagens dela.")) return;
    await http.post(`/msg/threads/${aberta.thread.thread_id}/sair`);
    abrir(null); loadThreads();
  };

  const totalConvites = resumo.recebidos.length;

  return (
    <div className="p-4 md:p-8 lg:p-10 max-w-6xl mx-auto" data-testid="mensagens-page">
      <header className="mb-5">
        <div className="text-[11px] font-bold tracking-[0.28em] uppercase text-emerald-brand mb-2 flex items-center gap-2">
          <MessageSquare className="w-3.5 h-3.5" strokeWidth={2.25} /> Chat
        </div>
        <h1 className="font-display text-3xl font-bold text-stone-900">Conversas da equipe e de parceiros</h1>
        <Dica className="text-sm text-stone-500 mt-1 max-w-2xl">{`Fale com quem você cadastrou em Equipe e com colegas vinculados (Terceiros). Sem arquivos; guardamos por 90 dias.`}</Dica>
      </header>

      <Segmented fit className="mb-4" value={aba} onChange={setAba} testid="msg-abas"
        options={[["conversas", "Conversas"], ["vinculos", "Vínculos"]].map(([k, label]) => ({
          key: k, testid: `msg-aba-${k}`,
          label: <>{label}{k === "vinculos" && totalConvites > 0 && <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-gold-500 text-stone-950 text-[11px] font-bold">{totalConvites}</span>}</>,
        }))} />

      {aba === "vinculos" ? (
        <Vinculos resumo={resumo} reload={() => { loadResumo(); refresh(); }} />
      ) : (
        <div className="grid md:grid-cols-[320px_1fr] gap-4 items-start">
          {/* lista — some no celular quando há conversa aberta */}
          <section className={`${threadId ? "hidden md:block" : ""} bg-white border border-stone-200 rounded-md overflow-hidden`}>
            <div className="p-3 border-b border-stone-100 flex items-center justify-between">
              <span className="text-sm font-semibold text-stone-800">Conversas</span>
              <Button data-testid="msg-nova" size="sm" onClick={() => setNovaOpen(true)} className="gap-1.5 bg-stone-900 hover:bg-stone-800 text-white">
                <Plus className="w-3.5 h-3.5" /> Nova
              </Button>
            </div>
            {threads === null ? (
              <div className="p-6 text-sm text-stone-500">Carregando…</div>
            ) : threads.length === 0 ? (
              <div className="p-6 text-sm text-stone-500" data-testid="msg-vazio">Nenhuma conversa ainda. Toque em “Nova” para falar com alguém da Equipe ou com um Terceiro.</div>
            ) : (
              <ul className="divide-y divide-stone-100 max-h-[70vh] overflow-y-auto">
                {threads.map((t) => (
                  <li key={t.thread_id}>
                    <button data-testid={`msg-thread-${t.thread_id}`} onClick={() => abrir(t.thread_id)}
                      className={`w-full text-left px-3 py-3 hover:bg-stone-50 ${threadId === t.thread_id ? "bg-stone-50" : ""}`}>
                      <div className="flex items-center gap-2">
                        {t.kind === "group" && <Users className="w-3.5 h-3.5 text-stone-400 shrink-0" />}
                        <span className={`text-sm truncate flex-1 ${t.unread ? "font-bold text-stone-900" : "font-medium text-stone-800"}`}>{t.title}</span>
                        <span className="text-[11px] text-stone-400 tabular-nums">{fmtHora(t.last_at)}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-stone-500 truncate flex-1">{t.last_preview ? `${t.last_sender ? t.last_sender.split(" ")[0] + ": " : ""}${t.last_preview}` : "Sem mensagens"}</span>
                        {t.unread > 0 && <span data-testid={`msg-unread-${t.thread_id}`} className="min-w-[18px] h-[18px] px-1 rounded-full bg-gold-500 text-stone-950 text-[11px] font-bold inline-flex items-center justify-center">{t.unread}</span>}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* conversa */}
          <section className={`${threadId ? "" : "hidden md:flex"} bg-white border border-stone-200 rounded-md flex-col min-h-[60vh] md:min-h-[65vh]`}>
            {!aberta ? (
              <div className="m-auto p-8 text-sm text-stone-400 text-center">Escolha uma conversa.</div>
            ) : (
              <>
                <div className="p-3 border-b border-stone-100 flex items-center gap-2">
                  <button className="md:hidden p-1 text-stone-500" data-testid="msg-voltar" onClick={() => abrir(null)} aria-label="Voltar"><ArrowLeft className="w-5 h-5" /></button>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-stone-900 truncate" data-testid="msg-titulo">{aberta.thread.title}</div>
                    {aberta.thread.kind === "group" && (
                      <div className="text-[11px] text-stone-500 truncate">{[...aberta.thread.members.map((m) => m.name), ...aberta.thread.externos.map((e) => `${e.name} (e-mail)`)].join(", ")}</div>
                    )}
                    {aberta.thread.kind === "dm" && aberta.thread.externos.length > 0 && <div className="text-[11px] text-stone-500">Sem conta no app — vai por e-mail</div>}
                  </div>
                  {aberta.thread.kind === "group" && (
                    <Button variant="ghost" size="sm" onClick={sair} title="Sair do grupo" data-testid="msg-sair"><LogOut className="w-4 h-4" /></Button>
                  )}
                </div>
                <div className="flex-1 overflow-y-auto p-3 space-y-2 max-h-[55vh]" data-testid="msg-lista">
                  {aberta.messages.length === 0 && <p className="text-xs text-stone-400 text-center mt-6">Nenhuma mensagem ainda.</p>}
                  {aberta.messages.map((m) => (
                    <div key={m.msg_id} className={`flex ${m.mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${m.mine ? "bg-[#d9fdd3] text-stone-900" : "bg-stone-100 text-stone-900"}`}>
                        {!m.mine && aberta.thread.kind === "group" && <div className="text-[11px] font-semibold text-emerald-brand mb-0.5">{m.sender_name}</div>}
                        {m.text && <div className="whitespace-pre-wrap break-words">{m.text}</div>}
                        {m.anexo && (
                          <div className={`mt-1.5 rounded border px-2 py-1.5 text-xs flex items-start gap-1.5 ${m.mine ? "border-green-300 bg-green-50" : "border-stone-300 bg-white"}`} data-testid="msg-anexo">
                            {m.anexo.tipo === "processo" ? <FolderKanban className="w-3.5 h-3.5 mt-0.5 shrink-0" /> : m.anexo.tipo === "publicacao" ? <Inbox className="w-3.5 h-3.5 mt-0.5 shrink-0" /> : <CalendarClock className="w-3.5 h-3.5 mt-0.5 shrink-0" />}
                            <span className="break-words">{m.anexo.titulo}</span>
                          </div>
                        )}
                        <div className={`text-[10px] mt-1 tabular-nums ${m.mine ? "text-green-800/60" : "text-stone-500"}`}>
                          {fmtHora(m.created_at)}{m.mine && m.seen ? " · Visto" : ""}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={fimRef} />
                </div>
                <div className="p-3 border-t border-stone-100 space-y-2">
                  {anexo && (
                    <div className="text-xs bg-stone-50 border border-stone-200 rounded px-2 py-1.5 flex items-center gap-2" data-testid="msg-anexo-previa">
                      <Paperclip className="w-3.5 h-3.5" /><span className="flex-1 break-words">{anexo.titulo}</span>
                      <button onClick={() => setAnexo(null)} aria-label="Remover anexo"><X className="w-3.5 h-3.5" /></button>
                    </div>
                  )}
                  <Textarea data-testid="msg-texto" rows={2} maxLength={MAX} value={texto} onChange={(e) => setTexto(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) enviar(); }}
                    placeholder="Escreva sua mensagem…" />
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => setAnexoOpen(true)} data-testid="msg-anexar" className="gap-1.5"><Paperclip className="w-3.5 h-3.5" /> Anexar</Button>
                    <span className={`text-[11px] tabular-nums ${texto.length > MAX - 100 ? "text-rose-600" : "text-stone-400"}`}>{texto.length}/{MAX}</span>
                    <Button data-testid="msg-enviar" size="sm" disabled={enviando || (!texto.trim() && !anexo)} onClick={enviar} className="ml-auto gap-1.5 bg-emerald-brand text-white">
                      <Send className="w-3.5 h-3.5" /> Enviar
                    </Button>
                  </div>
                  <p className="text-[11px] text-stone-400">Evite dados de clientes além do necessário. Ctrl/⌘+Enter envia.</p>
                </div>
              </>
            )}
          </section>
        </div>
      )}

      <NovaConversa open={novaOpen} onClose={() => setNovaOpen(false)} resumo={resumo}
        onCriada={(t) => { setNovaOpen(false); loadThreads(); abrir(t.thread_id); }} />
      <AnexarDialog open={anexoOpen} onClose={() => setAnexoOpen(false)} onPick={(a) => { setAnexo(a); setAnexoOpen(false); }} />
    </div>
  );
}

function NovaConversa({ open, onClose, resumo, onCriada }) {
  const [user_ids, setU] = useState([]);
  const [member_ids, setM] = useState([]);
  const [nome, setNome] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setU([]); setM([]); setNome(""); } }, [open]);
  const toggle = (arr, set, v) => set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  // membro da Equipe já vinculado aparece como Terceiro — evita escolher a mesma pessoa duas vezes
  const equipeSemVinculo = resumo.equipe.filter((m) => !m.vinculado_user_id);
  const total = user_ids.length + member_ids.length;
  const criar = async () => {
    setBusy(true);
    try {
      const r = await http.post("/msg/threads", { user_ids, member_ids, name: total > 1 ? nome : null });
      onCriada(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Não foi possível criar a conversa"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Nova conversa</DialogTitle></DialogHeader>
        <div className="space-y-4 max-h-[55vh] overflow-y-auto">
          <div>
            <div className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1.5">Terceiros (vinculados)</div>
            {resumo.terceiros.length === 0 && <p className="text-xs text-stone-400">Nenhum vínculo aceito ainda — veja a aba Vínculos.</p>}
            {resumo.terceiros.map((t) => (
              <label key={t.user_id} className="flex items-center gap-2 py-1.5 text-sm cursor-pointer">
                <Checkbox data-testid={`nova-ter-${t.user_id}`} checked={user_ids.includes(t.user_id)} onCheckedChange={() => toggle(user_ids, setU, t.user_id)} />
                <span>{t.name}</span><span className="text-xs text-stone-400">{t.email}</span>
              </label>
            ))}
          </div>
          <div>
            <div className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1.5">Equipe</div>
            {resumo.equipe.length === 0 && <p className="text-xs text-stone-400">Ninguém cadastrado na aba Equipe.</p>}
            {equipeSemVinculo.map((m) => (
              <label key={m.member_id} className={`flex items-center gap-2 py-1.5 text-sm ${m.email ? "cursor-pointer" : "opacity-50"}`}>
                <Checkbox data-testid={`nova-eq-${m.member_id}`} disabled={!m.email} checked={member_ids.includes(m.member_id)} onCheckedChange={() => toggle(member_ids, setM, m.member_id)} />
                <span>{m.name}</span>
                <span className="text-xs text-stone-400">{m.email ? "por e-mail" : "sem e-mail"}</span>
              </label>
            ))}
            {resumo.equipe.some((m) => m.vinculado_user_id) && <p className="text-[11px] text-stone-400 mt-1">Quem da Equipe já está vinculado aparece em Terceiros.</p>}
          </div>
          {total > 1 && (
            <div>
              <Label className="text-xs text-stone-500 mb-1 block">Nome do grupo</Label>
              <Input data-testid="nova-nome" maxLength={80} value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Ex: Parceria Processo X" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button data-testid="nova-criar" disabled={busy || total < 1 || (total > 1 && !nome.trim())} onClick={criar} className="bg-stone-900 text-white">Abrir conversa</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AnexarDialog({ open, onClose, onPick }) {
  const [comp, setComp] = useState([]);
  const [procs, setProcs] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => {
    if (!open) return;
    setQ("");
    http.get("/hearings/upcoming").then((r) => setComp(r.data)).catch(() => {});
    http.get("/processos").then((r) => setProcs(r.data)).catch(() => {});
  }, [open]);
  const f = q.trim().toLowerCase();
  // Só resumo vai na mensagem (tipo, data, hora, juízo, nº do processo) — nunca partes/cliente.
  const compItens = useMemo(() => comp.map((p) => {
    const h = p.hearing || {};
    const tipo = h.deadline_type || h.event_type || "compromisso";
    const titulo = [tipo.charAt(0).toUpperCase() + tipo.slice(1), h.date ? fmtData(h.date) : null, h.time, h.case_number].filter(Boolean).join(" · ");
    return { tipo: "compromisso", id: p.pub_id, titulo };
  }).filter((x) => !f || x.titulo.toLowerCase().includes(f)).slice(0, 40), [comp, f]);
  const procItens = useMemo(() => procs.map((p) => ({ tipo: "processo", id: p.processo_id, titulo: `Processo · ${p.numero}` }))
    .filter((x) => !f || x.titulo.toLowerCase().includes(f)).slice(0, 40), [procs, f]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Anexar compromisso ou processo</DialogTitle></DialogHeader>
        <Input data-testid="anexar-busca" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por data, tipo ou número…" />
        <p className="text-[11px] text-stone-400">Vai só o resumo (sem nome de cliente nem partes).</p>
        <div className="max-h-[50vh] overflow-y-auto space-y-1">
          {[...compItens, ...procItens].length === 0 && <p className="text-xs text-stone-400 py-4 text-center">Nada encontrado.</p>}
          {compItens.map((x) => <ItemAnexo key={`c${x.id}`} x={x} onPick={onPick} icon={CalendarClock} />)}
          {procItens.map((x) => <ItemAnexo key={`p${x.id}`} x={x} onPick={onPick} icon={FolderKanban} />)}
        </div>
      </DialogContent>
    </Dialog>
  );
}
const ItemAnexo = ({ x, onPick, icon: Icon }) => (
  <button data-testid={`anexar-${x.tipo}-${x.id}`} onClick={() => onPick(x)} className="w-full text-left flex items-start gap-2 px-2 py-2 rounded hover:bg-stone-50 text-sm">
    <Icon className="w-4 h-4 mt-0.5 text-stone-400 shrink-0" /><span className="break-words">{x.titulo}</span>
  </button>
);

function Vinculos({ resumo, reload }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const convidar = async (e, quem) => {
    setBusy(true);
    try {
      await http.post("/msg/vinculos", { email: e });
      toast.success("Convite enviado. Se a pessoa tiver conta no IApauta, ela será avisada.");
      if (!quem) setEmail("");
      reload();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "E-mail inválido");
    } finally { setBusy(false); }
  };
  const acao = async (id, a, msg) => { await http.post(`/msg/vinculos/${id}/${a}`); toast.success(msg); reload(); };
  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="msg-vinculos">
      <section className="bg-white border border-stone-200 rounded-md p-4 space-y-3">
        <h2 className="font-display text-lg font-semibold text-stone-900 flex items-center gap-2"><UserPlus className="w-4 h-4 text-emerald-brand" /> Vincular um colega (Terceiro)</h2>
        <Dica className="text-xs text-stone-500">{`Para conversar e compartilhar compromissos em processos feitos em parceria. O colega precisa ter conta no IApauta e aceitar; qualquer um dos dois pode desfazer depois.`}</Dica>
        <div className="flex gap-2">
          <Input data-testid="vinc-email" type="email" placeholder="e-mail do colega no IApauta" value={email} onChange={(e) => setEmail(e.target.value)} />
          <Button data-testid="vinc-enviar" disabled={busy || !email.includes("@")} onClick={() => convidar(email)} className="bg-stone-900 text-white">Convidar</Button>
        </div>
        {resumo.enviados.length > 0 && (
          <div>
            <div className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Aguardando resposta</div>
            {resumo.enviados.map((l) => (
              <div key={l.link_id} className="flex items-center gap-2 text-sm py-1"><span className="flex-1 truncate">{l.to_email}</span>
                <Button size="sm" variant="ghost" onClick={() => acao(l.link_id, "desfazer", "Convite cancelado")}>Cancelar</Button></div>
            ))}
          </div>
        )}
        {resumo.equipe.some((m) => m.email && !m.vinculado_user_id) && (
          <div>
            <div className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Da sua Equipe, com e-mail</div>
            {resumo.equipe.filter((m) => m.email && !m.vinculado_user_id).map((m) => (
              <div key={m.member_id} className="flex items-center gap-2 text-sm py-1"><span className="flex-1 truncate">{m.name} <span className="text-xs text-stone-400">{m.email}</span></span>
                <Button size="sm" variant="outline" disabled={busy} onClick={() => convidar(m.email, true)}>Vincular no app</Button></div>
            ))}
          </div>
        )}
      </section>
      <section className="bg-white border border-stone-200 rounded-md p-4 space-y-4">
        <div>
          <h2 className="font-display text-lg font-semibold text-stone-900 mb-2">Convites recebidos</h2>
          {resumo.recebidos.length === 0 ? <p className="text-sm text-stone-500">Nenhum convite pendente.</p> : resumo.recebidos.map((l) => (
            <div key={l.link_id} data-testid={`vinc-recebido-${l.link_id}`} className="flex items-center gap-2 text-sm py-1.5">
              <span className="flex-1 min-w-0"><strong>{l.from_name}</strong> <span className="text-xs text-stone-400">{l.from_email}</span></span>
              <Button size="sm" data-testid={`vinc-aceitar-${l.link_id}`} className="gap-1 bg-emerald-600 text-white" onClick={() => acao(l.link_id, "aceitar", "Vínculo aceito")}><Check className="w-3.5 h-3.5" /> Aceitar</Button>
              <Button size="sm" variant="outline" onClick={() => acao(l.link_id, "recusar", "Convite recusado")}><X className="w-3.5 h-3.5" /></Button>
            </div>
          ))}
        </div>
        <div>
          <h2 className="font-display text-lg font-semibold text-stone-900 mb-2">Terceiros vinculados</h2>
          {resumo.terceiros.length === 0 ? <p className="text-sm text-stone-500">Ninguém vinculado ainda.</p> : resumo.terceiros.map((t) => (
            <div key={t.link_id} className="flex items-center gap-2 text-sm py-1.5">
              <span className="flex-1 min-w-0 truncate">{t.name} <span className="text-xs text-stone-400">{t.email}</span></span>
              <Button size="sm" variant="ghost" className="text-rose-700" onClick={() => window.confirm(`Desfazer o vínculo com ${t.name}? As conversas deixam de funcionar.`) && acao(t.link_id, "desfazer", "Vínculo desfeito")}>Desfazer</Button>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
